import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token   = localStorage.getItem('accessToken');

export default function AttorneyPayments({ caseId, sofaResponse }) {
  const [payments, setPayments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [newPayment, setNewPayment] = useState({
    transaction_id: '',
    name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip_code: '',
    email: '',
    third_party_payor: '',
    service_description: '',
    service_value: '',
    payment_date: '',
    payment_amount: ''
  });
  const [editPaymentData, setEditPaymentData] = useState(null);

  // Fetch existing payments
  useEffect(() => {
    if (!sofaResponse) return;
    fetch(`${API_URL}/cases/${caseId}/sofa-attorney-payments/?sofa_response=${sofaResponse.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(setPayments)
      .catch(console.error);
  }, [caseId, sofaResponse]);

  function openAddModal() {
    setNewPayment({
      transaction_id: '',
      name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip_code: '',
      email: '',
      third_party_payor: '',
      service_description: '',
      service_value: '',
      payment_date: '',
      payment_amount: ''
    });
    setModalOpen(true);
  }

  function openEditModal(item) {
    setEditPaymentData(item);
    setEditModalOpen(true);
  }

  async function handleSaveNew() {
    try {
      const res = await fetch(`${API_URL}/cases/${caseId}/sofa-attorney-payments/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newPayment)
      });
      if (!res.ok) throw new Error();
      const created = await res.json();
      setPayments(prev => [...prev, created]);
      setModalOpen(false);
    } catch (err) {
      console.error('Failed to add payment', err);
    }
  }

  async function handleSaveEdit() {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-attorney-payments/${editPaymentData.id}/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(editPaymentData)
        }
      );
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setPayments(prev =>
        prev.map(p => (p.id === updated.id ? updated : p))
      );
      setEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update payment', err);
    }
  }

  async function handleDelete(id) {
    try {
      const res = await fetch(
        `${API_URL}/cases/${caseId}/sofa-attorney-payments/${id}/`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!res.ok) throw new Error();
      setPayments(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Failed to delete payment', err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">
        Question 16: Payments to Bankruptcy Attorneys or Preparers
      </h2>
      <p className="text-sm text-gray-600 mb-4">
            List payments made to an attorney or bankruptcy preparer within 1 year of filing.
        </p>
      {payments.length === 0 ? (
        <p>No attorney payments found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {payments.map(item => (
            <div
              key={item.id}
              className="relative p-4 bg-white shadow rounded cursor-pointer hover:shadow-md transition"
              onClick={() => openEditModal(item)}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleDelete(item.id);
                }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                {/* reuse your standard trash icon here */}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                    2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                    6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                    2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                    0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                    00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">{item.name}</h3>
              <p className="mt-1 text-sm"><strong>ID:</strong> {item.transaction_id || '—'}</p>
              <p className="mt-1 text-sm">
                {item.street1}{item.street2 ? `, ${item.street2}` : ''},
                {item.city}, {item.state} {item.zip_code}
              </p>
              <p className="mt-1 text-sm"><strong>Amount:</strong> ${item.payment_amount}</p>
              <p className="mt-1 text-sm"><strong>Date:</strong> {item.payment_date}</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={openAddModal}
        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        Add Attorney Payment
      </button>

      {/* Add Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl">
            <h3 className="text-lg font-semibold mb-4">Add Attorney Payment</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={newPayment.name}
                onChange={e => setNewPayment(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newPayment.street1}
                  onChange={e => setNewPayment(prev => ({ ...prev, street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newPayment.street2}
                  onChange={e => setNewPayment(prev => ({ ...prev, street2: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newPayment.city}
                  onChange={e => setNewPayment(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newPayment.state}
                  onChange={e => setNewPayment(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={newPayment.zip_code}
                  onChange={e => setNewPayment(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Service Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={newPayment.service_description}
                onChange={e => setNewPayment(prev => ({ ...prev, service_description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Amount</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={newPayment.payment_amount}
                  onChange={e => setNewPayment(prev => ({ ...prev, payment_amount: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={newPayment.payment_date}
                  onChange={e => setNewPayment(prev => ({ ...prev, payment_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end mt-6 space-x-2">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">
                Cancel
              </button>
              <button onClick={handleSaveNew} className="px-4 py-2 bg-blue-600 text-white rounded">
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal (identical layout but bound to editPaymentData) */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl">
            <h3 className="text-lg font-semibold mb-4">Edit Attorney Payment</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                className="w-full border rounded px-2 py-1"
                value={editPaymentData.name || ''}
                onChange={e => setEditPaymentData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Street 1</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editPaymentData.street1 || ''}
                  onChange={e => setEditPaymentData(prev => ({ ...prev, street1: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Street 2</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editPaymentData.street2 || ''}
                  onChange={e => setEditPaymentData(prev => ({ ...prev, street2: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-7 gap-4 mb-3">
              <div className="col-span-4">
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editPaymentData.city || ''}
                  onChange={e => setEditPaymentData(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-sm font-medium mb-1">State</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editPaymentData.state || ''}
                  onChange={e => setEditPaymentData(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Zip Code</label>
                <input
                  type="text"
                  className="w-full border rounded px-2 py-1"
                  value={editPaymentData.zip_code || ''}
                  onChange={e => setEditPaymentData(prev => ({ ...prev, zip_code: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Service Description</label>
              <textarea
                className="w-full border rounded px-2 py-1"
                value={editPaymentData.service_description || ''}
                onChange={e => setEditPaymentData(prev => ({ ...prev, service_description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Payment Amount</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={editPaymentData.payment_amount || ''}
                  onChange={e => setEditPaymentData(prev => ({ ...prev, payment_amount: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Payment Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={editPaymentData.payment_date || ''}
                  onChange={e => setEditPaymentData(prev => ({ ...prev, payment_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end mt-6 space-x-2">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">
                Cancel
              </button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}